import jsPDF from "jspdf";

const NAVY = [20, 38, 74];
const BLUE = [30, 127, 212];
const GOLD = [245, 183, 0];
const ADDRESS = ["DMCC Free Zone, Unit No: 3O-01-5237", "Jewellery & Gemplex 3, Plot No: DMCC-PH2-J&GPlexS", "Jewellery & Gemplex, Dubai, United Arab Emirates"];
const CONTACTS = ["ops@heliopolispharmaceuticals.com", "Joseph Gadalla, COO · +971 54 774 1436", "John Kairy, CRM Manager · +971 50 534 8137", "www.heliopolispharmaceuticals.com"];
const TERMS = "Prices are quoted ex-works unless stated otherwise and are valid until the date shown above. Lead times are indicative and subject to product availability at time of order. All products are supplied with full documentation and cold-chain handling where applicable.";
const BODY = 8.5;
export const PDF_COLUMNS = [["origin", "Origin"], ["from", "Shipped from"], ["lead", "Lead time"], ["qty", "Qty"], ["unit", "Unit price"], ["total", "Line total"]];

const loadLogo = () => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = "/heliopolis-logo.png";
});

const num2 = (v) => Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CUR_SYM = { USD: "$", EUR: "€", EGP: "EGP " };
export const money = (v, cur) => `${cur || "USD"} ${num2(v)}`;
export const moneySym = (v, cur) => `${CUR_SYM[cur] || "$"}${num2(v)}`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }) : "—");
const itemTitle = (ln) => [ln.name, ln.form, ln.strength, ln.pack].filter(Boolean).join(" · ");

// Generic branded document renderer used by quote / proforma / supplier PO.
async function renderDoc({ title, meta, party, columns, rows, totals, blocks, signLeft, signRight, fileName, open }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 16, R = W - M, BOTTOM = 270;
  const logo = await loadLogo();
  let y = 14;

  const header = () => {
    y = 14;
    if (logo) doc.addImage(logo, "PNG", M - 2, y - 4, 40, 40 * (logo.naturalHeight || 498) / (logo.naturalWidth || 712));
    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(...NAVY);
    doc.text(title, R, y + 4, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(BODY); doc.setTextColor(60);
    meta.forEach((t, i) => doc.text(t, R, y + 11 + i * 4.6, { align: "right" }));
    [...ADDRESS, CONTACTS[0]].forEach((t, i) => doc.text(t, M + 42, y + 10 + i * 4.6));
    y += 40;
    doc.setDrawColor(...GOLD); doc.setLineWidth(1); doc.line(M, y, R, y);
    doc.setDrawColor(...BLUE); doc.setLineWidth(0.4); doc.line(M, y + 1.6, R, y + 1.6);
    y += 9;
  };
  const footer = (page, pages) => {
    doc.setDrawColor(220); doc.setLineWidth(0.3); doc.line(M, 280, R, 280);
    doc.setFont("helvetica", "normal"); doc.setFontSize(BODY); doc.setTextColor(120);
    doc.text("Heliopolis Pharmaceuticals · DMCC Free Zone, Unit 3O-01-5237, Jewellery & Gemplex 3, Dubai, UAE", M, 284.5);
    doc.text(CONTACTS.slice(0, 3).join("  ·  "), M, 288.5);
    doc.text(`Page ${page} of ${pages}`, R, 288.5, { align: "right" });
  };
  const tableHead = () => {
    doc.setFillColor(...NAVY); doc.rect(M, y - 4.5, R - M, 7, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(BODY); doc.setTextColor(255);
    columns.forEach((c) => doc.text(c.label, c.x + (c.align === "right" ? 0 : c.pad || 0), y, { align: c.align || "left" }));
    y += 7;
  };
  const ensure = (need) => { if (y + need > BOTTOM) { doc.addPage(); header(); tableHead(); } };

  header();

  // Party block(s)
  doc.setFont("helvetica", "bold"); doc.setFontSize(BODY); doc.setTextColor(...BLUE);
  doc.text(party.label, M, y);
  if (party.rightLabel) doc.text(party.rightLabel, 120, y);
  doc.setTextColor(20);
  doc.text(party.name || "", M, y + 5.5);
  doc.setFont("helvetica", "normal"); doc.setTextColor(60);
  const partyLines = party.lines.flatMap((t) => (t ? doc.splitTextToSize(String(t), 90) : []));
  partyLines.forEach((t, i) => doc.text(t, M, y + 10.5 + i * 4.5));
  if (party.rightLabel) { doc.setTextColor(20); doc.setFont("helvetica", "bold"); doc.text(party.rightValue || "—", 120, y + 5.5); }
  y += 10.5 + Math.max(partyLines.length, 1) * 4.5 + 6;

  tableHead();
  doc.setFont("helvetica", "normal"); doc.setTextColor(20);
  rows.forEach((row, idx) => {
    const cells = columns.map((c) => doc.splitTextToSize(String(row[c.key] ?? ""), c.w));
    const n = Math.max(...cells.map((c) => c.length), 1);
    const h = n * 4.2 + 3.5;
    ensure(h);
    if (idx % 2 === 1) { doc.setFillColor(246, 248, 252); doc.rect(M, y - 3.5, R - M, h, "F"); }
    columns.forEach((c, i) => {
      doc.setFontSize(BODY); doc.setTextColor(20);
      doc.text(cells[i], c.x + (c.align === "right" ? 0 : c.pad || 0), y, { align: c.align || "left" });
    });
    y += h;
    doc.setDrawColor(230); doc.setLineWidth(0.2); doc.line(M, y - 3, R, y - 3);
  });

  if (totals) {
    ensure(40); y += 3;
    const tRow = (label, val, bold = false) => {
      doc.setFont("helvetica", bold ? "bold" : "normal"); doc.setFontSize(BODY); doc.setTextColor(bold ? 20 : 80);
      doc.text(label, 128, y); doc.text(val, R - 2, y, { align: "right" }); y += 5.5;
    };
    totals.rows.forEach(([l, v]) => tRow(l, v));
    doc.setDrawColor(...NAVY); doc.setLineWidth(0.5); doc.line(126, y - 3, R, y - 3); y += 1;
    tRow(totals.total[0], totals.total[1], true);
    if (totals.note) { doc.setFont("helvetica", "italic"); doc.setFontSize(BODY); doc.setTextColor(90); doc.text(totals.note, R - 2, y, { align: "right" }); y += 7; }
  }

  blocks.forEach(([t, text]) => {
    if (!text) return;
    const lines = doc.splitTextToSize(text, R - M);
    ensure(lines.length * 4.2 + 12); y += 4;
    doc.setFont("helvetica", "bold"); doc.setFontSize(BODY); doc.setTextColor(...BLUE); doc.text(t, M, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(50);
    doc.text(lines, M, y + 5); y += 5 + lines.length * 4.2;
  });

  ensure(30); y += 12;
  doc.setDrawColor(120); doc.setLineWidth(0.3);
  doc.line(M, y, M + 70, y); doc.line(R - 70, y, R, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(BODY); doc.setTextColor(90);
  doc.text(signLeft, M, y + 4.5); doc.text(signRight, R, y + 4.5, { align: "right" });

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) { doc.setPage(p); footer(p, pages); }
  if (open) window.open(doc.output("bloburl"), "_blank"); else doc.save(fileName);
}

const safe = (s) => (s || "client").replace(/[^a-z0-9]+/gi, "_");

const priceColumns = (cur, hidden = []) => {
  const H = new Set(hidden);
  const M = 16, R = 194;
  const fixed = [
    { key: "origin", label: "ORIGIN", w: 20 },
    { key: "from", label: "SHIPPED FROM", w: 23 },
    { key: "lead", label: "LEAD TIME", w: 18 },
    { key: "qty", label: "QTY", w: 12, align: "right" },
    { key: "unit", label: `UNIT ${cur}`, w: 20, align: "right" },
    { key: "total", label: `TOTAL ${cur}`, w: 22, align: "right" },
  ].filter((c) => !H.has(c.key));
  const used = fixed.reduce((a, c) => a + c.w + 3, 0);
  const cols = [{ key: "item", label: "ITEM", x: M, pad: 2, w: R - M - used - 3 }];
  let x = M + cols[0].w + 3;
  fixed.forEach((c) => { cols.push({ ...c, x: c.align === "right" ? x + c.w : x }); x += c.w + 3; });
  return cols;
};
const priceRows = (lines) => lines.map((ln) => ({
  item: itemTitle(ln), origin: ln.origin || "—", from: ln.shipped_from || "—", lead: ln.lead_time || "—",
  qty: String(ln.quantity ?? ""), unit: num2(ln.unit_price), total: num2(Number(ln.quantity || 0) * Number(ln.unit_price || 0)),
}));
const priceTotals = (d, cur, label) => {
  const subtotal = (d.lines || []).reduce((a, l) => a + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const shipping = Number(d.shipping || 0), total = subtotal + shipping;
  const oc = d.other_currency || (cur === "USD" ? "EUR" : "USD");
  return {
    rows: [["Subtotal", money(subtotal, cur)], ...(shipping ? [["Shipping & handling", money(shipping, cur)]] : [])],
    total: [label, money(total, cur)],
    note: d.exchange_rate ? `Equivalent approx. ${money(total * Number(d.exchange_rate), oc)}  (1 ${cur} = ${d.exchange_rate} ${oc}, indicative)` : null,
  };
};

export async function quotePdf(q, { open = false } = {}) {
  const cur = q.currency || "USD";
  await renderDoc({
    title: "QUOTATION",
    meta: [`Quote No. ${q.quote_number || "DRAFT"}`, `Date: ${fmt(q.quote_date)}`, `Valid until: ${fmt(q.valid_until)}`, `Currency: ${cur}`],
    party: { label: "QUOTATION FOR", name: q.client_name, lines: [q.client_contact, q.client_email, q.client_address], rightLabel: "DESTINATION", rightValue: q.destination_country },
    columns: priceColumns(cur, q.pdf_hidden_columns || []), rows: priceRows(q.lines || []), totals: priceTotals(q, cur, "TOTAL"),
    blocks: [["PAYMENT TERMS", q.payment_terms], ["NOTES", q.notes], ["TERMS & CONDITIONS", TERMS]],
    signLeft: "For Heliopolis Pharmaceuticals — Sales Force", signRight: "Accepted by client (name, signature, date)",
    fileName: `Quotation-${q.quote_number || "draft"}-${safe(q.client_name)}.pdf`, open,
  });
}

export async function proformaPdf(pf, { open = false } = {}) {
  const cur = pf.currency || "USD";
  await renderDoc({
    title: "PROFORMA INVOICE",
    meta: [`Proforma No. ${pf.proforma_number || "DRAFT"}`, `Ref. quote ${pf.quote_number || ""}`, `Date: ${fmt(pf.proforma_date)}`, `Valid until: ${fmt(pf.valid_until)}`, `Currency: ${cur}`],
    party: { label: "BILL TO", name: pf.client_name, lines: [pf.client_contact, pf.client_email, pf.client_address], rightLabel: "DESTINATION", rightValue: pf.destination_country },
    columns: priceColumns(cur), rows: priceRows(pf.lines || []), totals: priceTotals(pf, cur, "AMOUNT DUE"),
    blocks: [["PAYMENT TERMS", pf.payment_terms], ["BANK DETAILS", pf.bank_details], ["NOTES", pf.notes], ["TERMS & CONDITIONS", TERMS]],
    signLeft: "For Heliopolis Pharmaceuticals — Sales Force", signRight: "Accepted by client (name, signature, date)",
    fileName: `Proforma-${pf.proforma_number || "draft"}-${safe(pf.client_name)}.pdf`, open,
  });
}

const finalTotals = (fi, cur) => {
  const subtotal = (fi.lines || []).reduce((a, l) => a + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const shipping = Number(fi.shipping || 0), pct = Number(fi.commission_pct || 0), commission = subtotal * pct / 100, transfer = Number(fi.internal_transfer || 0);
  const total = subtotal + shipping + commission + transfer;
  const oc = fi.other_currency || (cur === "USD" ? "EUR" : "USD");
  return {
    rows: [["Subtotal", money(subtotal, cur)], ...(shipping ? [["Shipping & handling", money(shipping, cur)]] : []), ...(pct ? [[`Commission (${pct}%)`, money(commission, cur)]] : []),
      ...(transfer ? [[`Internal transfers${fi.internal_transfer_note ? ` — ${fi.internal_transfer_note}` : ""}`, money(transfer, cur)]] : [])],
    total: ["AMOUNT DUE", money(total, cur)],
    note: fi.exchange_rate ? `Equivalent approx. ${money(total * Number(fi.exchange_rate), oc)}  (1 ${cur} = ${fi.exchange_rate} ${oc}, indicative)` : null,
  };
};

export async function finalInvoicePdf(fi, { open = false } = {}) {
  const cur = fi.currency || "USD";
  const lines = (fi.lines || []).map((l) => ({ ...l, lead_time: l.ordered != null ? `${Number(l.ordered)} ordered` : "" }));
  await renderDoc({
    title: "COMMERCIAL INVOICE",
    meta: [`Invoice No. ${fi.invoice_number || "DRAFT"}`, `Ref. proforma ${fi.proforma_number || ""}`, `Date: ${fmt(fi.invoice_date)}`, ...(fi.due_date ? [`Due: ${fmt(fi.due_date)}`] : []), `Currency: ${cur}`, "Based on quantities received"],
    party: { label: "BILL TO", name: fi.client_name, lines: [fi.client_contact, fi.client_email, fi.client_address], rightLabel: "DESTINATION", rightValue: fi.destination_country },
    columns: priceColumns(cur, ["from"]), rows: priceRows(lines), totals: finalTotals(fi, cur),
    blocks: [["PAYMENT TERMS", fi.payment_terms], ["BANK DETAILS", fi.bank_details], ["NOTES", fi.notes], ["TERMS & CONDITIONS", TERMS]],
    signLeft: "For Heliopolis Pharmaceuticals — Sales Force", signRight: "Received by client (name, signature, date)",
    fileName: `Invoice-${fi.invoice_number || "draft"}-${safe(fi.client_name)}.pdf`, open,
  });
}

export async function poPdf(po, { open = false } = {}) {
  await renderDoc({
    title: "PURCHASE ORDER",
    meta: [`PO No. ${po.po_number || "DRAFT"}`, `Date: ${fmt(po.po_date)}`, ...(po.delivery_date ? [`Requested delivery: ${fmt(po.delivery_date)}`] : [])],
    party: { label: "SUPPLIER", name: po.supplier_name, lines: [po.supplier_contact, po.supplier_email, po.supplier_address] },
    columns: [
      { key: "n", label: "#", x: 16, pad: 2, w: 8 },
      { key: "name", label: "ITEM NAME", x: 28, w: 88 },
      { key: "form", label: "FORM", x: 120, w: 30 },
      { key: "strength", label: "STRENGTH", x: 152, w: 26 },
      { key: "qty", label: "QTY", x: 192, w: 14, align: "right" },
    ],
    rows: (po.lines || []).map((ln, i) => ({ n: String(i + 1), name: ln.name, form: ln.form || "—", strength: ln.strength || "—", qty: String(ln.quantity ?? "") })),
    totals: null,
    blocks: [["NOTES / INSTRUCTIONS", po.notes], ["DELIVERY", "Please deliver to Heliopolis Pharmaceuticals, DMCC Free Zone, Dubai, UAE unless otherwise instructed. Kindly confirm availability, batch/expiry and dispatch date by email to ops@heliopolispharmaceuticals.com."]],
    signLeft: "Authorised by Heliopolis Pharmaceuticals", signRight: "Confirmed by supplier (name, signature, date)",
    fileName: `PO-${po.po_number || "draft"}-${safe(po.supplier_name)}.pdf`, open,
  });
}
